import { useContext, useState } from 'react'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { ShieldCheck, RemoveUser, AlertErrorCircle, OverflowMenu } from '@/assets/icons'
import { getOrganizationAnalyticsId } from '@/analytics/utils/organization-analytics'
import { AuthContext } from '@/context/auth-context'
import OrgAdminConfirmationDialog, { OrgAdminConfirmationDialogProps } from './OrgAdminConfirmationDialog'
import { RemovalDialogContainer } from './RemovalDialogContainer'
import { useChangeRoleToAdminMutation, useChangeRoleToUserMutation } from '../apis/org-admin.api'
import { GlobalToastType, kProduction, kToastDurationMediumMs } from '@/constants/constants-ui'
import { useAppDispatch } from '@/store/store-hooks'
import { openGlobalToast } from '@/store/slices/global-toast.slice'
import { UserRole } from '../schemas'
import { MenuContent, MenuItemGroup } from '@/components/menu/MenuContent'
import { MenuItem, type MenuItemProps } from '@/components/menu/MenuItem'

type UserListItemMenuOption = Pick<MenuItemProps, 'label' | 'icon' | 'variant' | 'onSelect'>

type Props = {
  userId: string
  userFirebaseId: string | null
  userEmail: string
  userRole: UserRole | null
  isOrgPayer: boolean
  refreshData: () => void
  /** Raise confirmation dialog above another open modal. Default: false. */
  stacked?: boolean
}

const UserListItemMenu: React.FC<Props> = (props: Props) => {
  const { userId, userFirebaseId, userEmail, userRole, isOrgPayer, refreshData, stacked = false } = props
  const dispatch = useAppDispatch()

  // Auth Context
  const { user: currentUser, userAccountData } = useContext(AuthContext)
  const orgName = userAccountData?.orgData?.name ?? 'organization'
  const organizationId = getOrganizationAnalyticsId(userAccountData)

  // Dialog state
  const [openConfirmationDialogProps, setOpenConfirmationDialogProps] = useState<OrgAdminConfirmationDialogProps | null>(null)
  const [isRemovalModalOpen, setIsRemovalModalOpen] = useState(false)

  /// RTK-Query Mutations
  const [changeRoleToAdmin] = useChangeRoleToAdminMutation()
  const [changeRoleToUser] = useChangeRoleToUserMutation()

  const targetUserIsOrgPayer = isOrgPayer
  const targetUserIsCurrentUser = userFirebaseId === currentUser?.uid

  const menuOptions: Array<UserListItemMenuOption> = []

  // Any user can be promoted to admin
  if (userRole === UserRole.USER) {
    menuOptions.push({
      label: 'Grant admin access',
      icon: ShieldCheck,
      onSelect: () => {
        setOpenConfirmationDialogProps({
          title: 'Change role to admin',
          message: `Are you sure you want to change ${userEmail}'s role to admin?`,
          visible: true,
          onConfirm: async () => {
            await changeRoleToAdmin(userId).unwrap()
            refreshData()
            dispatch(openGlobalToast({ type: GlobalToastType.SUCCESS, message: `Role updated to Admin`, durationMs: kToastDurationMediumMs }))
          },
          closeDialog: () => setOpenConfirmationDialogProps(null),
          refreshData: refreshData,
          stacked,
        })
      },
    })
  }

  // Only non-payer admins can be demoted to user
  if (userRole === UserRole.ADMIN && !targetUserIsOrgPayer) {
    menuOptions.push({
      label: 'Change role to user',
      icon: ShieldCheck,
      onSelect: () => {
        setOpenConfirmationDialogProps({
          title: 'Change role to user',
          message: `Are you sure you want to change ${userEmail}'s role to user?`,
          visible: true,
          onConfirm: async () => {
            await changeRoleToUser(userId).unwrap()
            refreshData()
            dispatch(openGlobalToast({ type: GlobalToastType.SUCCESS, message: `Role updated to User`, durationMs: kToastDurationMediumMs }))
          },
          closeDialog: () => setOpenConfirmationDialogProps(null),
          refreshData: refreshData,
          stacked,
        })
      },
    })
  }

  // Only non-payers can be removed from the org
  if (!targetUserIsOrgPayer) {
    menuOptions.push({
      label: 'Remove from organization',
      icon: RemoveUser,
      variant: 'destructive',
      onSelect: () => setIsRemovalModalOpen(true),
    })
  }

  // Dev menu items
  if (!kProduction) {
    menuOptions.push({
      label: 'Dev: Log user',
      icon: AlertErrorCircle,
      onSelect: () => {
        console.log('user', { userId, userFirebaseId, userEmail, userRole, isOrgPayer })
      },
    })
  }

  // Hide the menu if the target user is the current user
  if (targetUserIsCurrentUser) {
    return <div className="w-7 h-7" />
  }

  return (
    <>
      {openConfirmationDialogProps && <OrgAdminConfirmationDialog {...openConfirmationDialogProps} />}
      {isRemovalModalOpen && (
        <RemovalDialogContainer
          userId={userId}
          userEmail={userEmail}
          organizationId={organizationId}
          orgName={orgName}
          refreshData={refreshData}
          onClose={() => setIsRemovalModalOpen(false)}
          stacked={stacked}
        />
      )}
      <DropdownMenu.Root>
        <DropdownMenu.Trigger
          disabled={menuOptions.length === 0}
          className="p-1 rounded-full text-brand-neutral-700 hocus:bg-brand-neutral-200 focus:outline-hidden data-[state=open]:bg-brand-neutral-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
          onPointerDown={(e) => e.stopPropagation()}
        >
          <OverflowMenu className="w-5 h-5" />
        </DropdownMenu.Trigger>

        <MenuContent className="min-w-[200px]" align="end" collisionPadding={8}>
          <MenuItemGroup>
            {menuOptions.map((item) => (
              <MenuItem key={item.label} label={item.label} icon={item.icon} variant={item.variant} onSelect={item.onSelect} />
            ))}
          </MenuItemGroup>
        </MenuContent>
      </DropdownMenu.Root>
    </>
  )
}

export default UserListItemMenu
